/**
 * Runtime environment abstraction for web, desktop (Electron), and staging.
 */

const DEFAULT_BACKEND = 'http://127.0.0.1:8000';

let _desktopBackendUrl = null;
let _desktopStorageBackend = null;

export function isDesktopApp() {
  if (typeof window !== 'undefined') {
    if (window.twinTales?.isDesktop) return true;
    if (window.location?.protocol === 'file:') return true;
  }
  return process.env.REACT_APP_DESKTOP === '1';
}

/** Desktop build targeting a remote HTTPS API (thin client / EC2). */
export function usesRemoteDesktopBackend() {
  if (!isDesktopApp()) return false;
  const url = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (!url || !/^https?:\/\//i.test(url)) return false;
  return !/^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?/i.test(url);
}

export function isDesktopBackendReady() {
  if (!isDesktopApp()) return true;
  if (usesRemoteDesktopBackend()) {
    return Boolean(_desktopBackendUrl || process.env.REACT_APP_BACKEND_URL);
  }
  return Boolean(_desktopBackendUrl);
}

export function setDesktopBackendBaseUrl(url) {
  _desktopBackendUrl = url ? url.replace(/\/+$/, '') : null;
}

export function setDesktopStorageBackend(mode) {
  _desktopStorageBackend = mode ? String(mode).trim().toLowerCase() : null;
}

export function desktopPrefersLocalStorage() {
  return isDesktopApp() && _desktopStorageBackend === 'local';
}

export function clearDesktopBackendBaseUrl() {
  _desktopBackendUrl = null;
  _desktopStorageBackend = null;
}

export function getBackendBaseUrl() {
  if (isDesktopApp()) {
    if (_desktopBackendUrl) return _desktopBackendUrl;
    if (usesRemoteDesktopBackend()) {
      return (process.env.REACT_APP_BACKEND_URL || DEFAULT_BACKEND).replace(/\/+$/, '');
    }
    throw new Error('Desktop backend is not ready yet');
  }
  const fromEnv = process.env.REACT_APP_BACKEND_URL;
  return (fromEnv || DEFAULT_BACKEND).replace(/\/+$/, '');
}

export function getApiBaseUrl() {
  return `${getBackendBaseUrl()}/api`;
}

export function getAppEnvironment() {
  if (process.env.REACT_APP_ENV === 'staging') return 'staging';
  if (process.env.NODE_ENV === 'production') return 'production';
  return 'development';
}
